import { format, unwrapFunctionStore } from 'svelte-i18n';
import { driver } from 'driver.js';

import { Loader } from '@client/components/loader';

import 'driver.js/dist/driver.css';

const $format = unwrapFunctionStore(format);

/**
 * The base configuration for all tours. This is a global configuration, meaning each tour and
 * all steps will inherit these settings.
 *
 * For more information on configuration options, refer to the documentation:
 * {@link https://driverjs.com/docs/configuration}
 *
 * @property {string} popoverClass - Allows for custom styling of the popover.
 * @property {Array<string>} showButtons - Buttons to display in the popover. Defaults to `["next", "previous", "close"]`. An empty array (`[]`) hides all buttons.
 * @property {string} doneBtnText - Text displayed on the "Done" button, used in the last step of a tour.
 * @property {string} nextBtnText - Text displayed on the "Next" button.
 * @property {string} prevBtnText - Text displayed on the "Previous" button.
 * @property {boolean} showProgress - Determines whether to display progress text in the popover.
 * @property {boolean} allowClose - Allows closing the popover by clicking on the backdrop. Defaults to `true`.
 * @property {boolean} allowKeyboardControl - Enables keyboard navigation. Defaults to `true`.
 * @property {boolean} disableActiveInteraction - Disables interaction with the highlighted element. Defaults to `false`.
 */
const TOURS_BASE_CONFIG = {
  popoverClass: 'os-driver',
  showButtons: ['next', 'previous'],
  doneBtnText: $format('label.DONE'),
  nextBtnText: '<i class="os-icon-arrow-right" />',
  prevBtnText: '<i class="os-icon-arrow-left" />',
  showProgress: false,
  allowClose: false,
  allowKeyboardControl: false,
  disableActiveInteraction: true
};

let globalEscapeListener = null;

/**
 * The global driver object used to create and manage all tours.
 *
 * For more information on the driver object and its hooks, refer to the documentation:2
 * {@link https://driverjs.com/docs/configuration}
 */
export const GLOBAL_DRIVER_OBJECT = driver({
  ...TOURS_BASE_CONFIG,
  onPopoverRender: () => {
    globalEscapeListener = (event) => {
      if (event.key === 'Escape') {
        GLOBAL_DRIVER_OBJECT.destroy();
        document.removeEventListener('keydown', globalEscapeListener);
      }
    };

    document.addEventListener('keydown', globalEscapeListener);

    const closeButton = document.createElement('button');

    closeButton.classList.add('driver-overlay-close-btn', 'os-driver');
    closeButton.innerHTML = '<i class="os-icon-x" />';
    closeButton.onclick = () => {
      if (
        !GLOBAL_DRIVER_OBJECT.hasNextStep() ||
        confirm('Are you sure you want to exit this tour? Your progress will not be saved.')
      ) {
        if (document.querySelector('.modal.show')) {
          document.querySelector('button.close').click();
        }

        closeButton.remove();
        GLOBAL_DRIVER_OBJECT.destroy();
      }
    };

    if (!document.querySelector('.driver-overlay-close-btn')) {
      document.body.appendChild(closeButton);
    }
  },
  onPrevClick: () => {
    if (globalEscapeListener) {
      document.removeEventListener('keydown', globalEscapeListener);
    }

    GLOBAL_DRIVER_OBJECT.movePrevious();
  },
  onNextClick: () => {
    if (globalEscapeListener) {
      document.removeEventListener('keydown', globalEscapeListener);
    }

    GLOBAL_DRIVER_OBJECT.moveNext();
  },
  onDestroyed: () => {
    if (globalEscapeListener) {
      document.removeEventListener('keydown', globalEscapeListener);
    }

    if (document.querySelector('.driver-overlay-close-btn')) {
      document.querySelector('.driver-overlay-close-btn').remove();
    }
  }
});
/**
 * Reusable tour step to indicate when pages, components, or other elements are loading.
 *
 * To use this step, import into the tour index file and add it to the steps array.
 *
 * Then update the `title` property of the `popover` object to match your specific use case.
 *
 * ```javascript
 *  import { GLOBAL_LOADER_STEP } from '@client/enums';
 *
 *  const stepsArray = [
 *    ...
 *    GLOBAL_LOADER_STEP,
 *    ...
 *  ];
 *
 *  stepsArray[stepIndex] = {
 *    ...stepsArray[stepIndex],
 *    popover: {
 *      ...stepsArray[stepIndex].popover,
 *      title: 'Your custom title here'
 *    }
 *  };
 * ```
 */
export const GLOBAL_LOADER_STEP = {
  popover: {
    title: '',
    description: '',
    showButtons: [],
    onPopoverRender: () => {
      const popoverBody = document.querySelector('#driver-popover-description');

      popoverBody.style.display = 'flex';
      popoverBody.style.justifyContent = 'center';
      popoverBody.style.paddingTop = '10px';
      popoverBody.textContent = '';

      const loaderInstance = new Loader({
        target: popoverBody,
        props: {
          show: true,
          inline: true
        }
      });

      setTimeout(() => {
        const interval = setInterval(() => {
          if (!document.querySelector('div.page > div.loader.medium')) {
            clearInterval(interval);

            loaderInstance.$destroy();

            GLOBAL_DRIVER_OBJECT.moveNext();
          }
        }, 100);
      }, 400);
    }
  }
};
